import { ReadFailed, type PrescriptionCopyData } from '../api/reads.js'
import type { WireMoney } from '../money.js'

/**
 * The portal's reads. `CUS-007`.
 *
 * **Not in `api/reads.ts`, because it is not that API.** Every function there carries a staff
 * session; the portal has none, and its credential is a bearer token read from the URL fragment and
 * sent in `X-Portal-Token`. Keeping the two apart means a staff token can never be sent to the
 * public routes by a helper that looked close enough, and the other way round.
 *
 * Reads only, so `OFF-001a` does not apply: nothing here is a write, and there is nothing to
 * queue. Mapped rather than cast, like every read in this app (`PROGRESS.md` problem 109): a field
 * the server does not send must be a message and not a white screen.
 */

export interface PortalConfig {
  readonly baseUrl: string
  /** The path segment the server resolves through the tenant directory. */
  readonly tenantRef: string
  readonly token: string
  readonly doFetch: typeof globalThis.fetch
}

export interface PortalOrderData {
  readonly id: string
  readonly number: string | null
  readonly status: string
  /** An ISO instant. */
  readonly placedAt: string
  readonly branchName: string
  readonly total: WireMoney
  readonly outstanding: WireMoney
}

export interface PortalEyeData {
  readonly sphere: number
  readonly cylinder: number
  readonly axis?: number
  readonly add?: number
}

export interface PortalPrescriptionData {
  readonly id: string
  readonly type: string
  readonly status: string
  readonly issueDate: string
  readonly expiryDate?: string
  readonly od: PortalEyeData
  readonly os: PortalEyeData
}

export interface PortalData {
  readonly name: string
  readonly orders: readonly PortalOrderData[]
  /** Absent when the link does not carry prescriptions. Absent is not the same as none. */
  readonly prescriptions?: readonly PortalPrescriptionData[]
}

export interface PortalOrderDetailData {
  readonly order: PortalOrderData
  readonly lines: readonly {
    readonly description: string
    readonly quantity: number
    readonly gross: WireMoney
  }[]
}

const mismatch = (field: string): ReadFailed =>
  new ReadFailed(
    500,
    'READ_CONTRACT',
    `The server's answer is missing ${field}. This is a version mismatch, not something you did.`,
  )

const rec = (value: unknown, field: string): Record<string, unknown> => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) throw mismatch(field)
  return value as Record<string, unknown>
}

const str = (value: unknown, field: string): string => {
  if (typeof value !== 'string') throw mismatch(field)
  return value
}

const num = (value: unknown, field: string): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw mismatch(field)
  return value
}

const list = (value: unknown, field: string): unknown[] => {
  if (!Array.isArray(value)) throw mismatch(field)
  return value as unknown[]
}

const optNum = (value: unknown, field: string): number | undefined =>
  value === undefined || value === null ? undefined : num(value, field)

const money = (value: unknown, field: string): WireMoney => {
  const shape = rec(value, field)
  return {
    minor: str(shape['minor'], `${field}.minor`),
    currency: str(shape['currency'], `${field}.currency`),
  }
}

const order = (value: unknown, field: string): PortalOrderData => {
  const o = rec(value, field)
  const number = o['number']
  if (number !== null && typeof number !== 'string') throw mismatch(`${field}.number`)
  return {
    id: str(o['id'], `${field}.id`),
    number,
    status: str(o['status'], `${field}.status`),
    placedAt: str(o['placedAt'], `${field}.placedAt`),
    branchName: str(o['branchName'], `${field}.branchName`),
    total: money(o['total'], `${field}.total`),
    outstanding: money(o['outstanding'], `${field}.outstanding`),
  }
}

const eye = (value: unknown, field: string): PortalEyeData => {
  const e = rec(value, field)
  const axis = optNum(e['axis'], `${field}.axis`)
  const add = optNum(e['add'], `${field}.add`)
  return {
    sphere: num(e['sphere'], `${field}.sphere`),
    cylinder: num(e['cylinder'], `${field}.cylinder`),
    ...(axis === undefined ? {} : { axis }),
    ...(add === undefined ? {} : { add }),
  }
}

export function toPortalData(body: unknown): PortalData {
  const b = rec(body, 'the portal answer')
  const prescriptions = b['prescriptions']
  return {
    name: str(rec(b['customer'], 'customer')['name'], 'customer.name'),
    orders: list(b['orders'], 'orders').map((o, i) => order(o, `orders[${String(i)}]`)),
    ...(prescriptions === undefined
      ? {}
      : {
          prescriptions: list(prescriptions, 'prescriptions').map((p, i) => {
            const rx = rec(p, `prescriptions[${String(i)}]`)
            const expiry = rx['expiryDate']
            return {
              id: str(rx['id'], 'prescription.id'),
              type: str(rx['type'], 'prescription.type'),
              status: str(rx['status'], 'prescription.status'),
              issueDate: str(rx['issueDate'], 'prescription.issueDate'),
              ...(typeof expiry === 'string' ? { expiryDate: expiry } : {}),
              od: eye(rx['od'], 'prescription.od'),
              os: eye(rx['os'], 'prescription.os'),
            }
          }),
        }),
  }
}

export function toPortalOrderDetail(body: unknown): PortalOrderDetailData {
  const b = rec(body, 'the order answer')
  return {
    order: order(b['order'], 'order'),
    lines: list(b['lines'], 'lines').map((l, i) => {
      const line = rec(l, `lines[${String(i)}]`)
      return {
        description: str(line['description'], 'line.description'),
        quantity: num(line['quantity'], 'line.quantity'),
        gross: money(line['gross'], 'line.gross'),
      }
    }),
  }
}

const request = async (config: PortalConfig, path: string): Promise<unknown> => {
  const response = await config.doFetch(
    `${config.baseUrl}/portal/${encodeURIComponent(config.tenantRef)}${path}`,
    { headers: { 'x-portal-token': config.token } },
  )
  if (!response.ok) {
    const body = (await response.json().catch(() => undefined)) as
      { code?: unknown; message?: unknown } | undefined
    throw new ReadFailed(
      response.status,
      typeof body?.code === 'string' ? body.code : 'READ_FAILED',
      typeof body?.message === 'string'
        ? body.message
        : `Request failed (${String(response.status)}).`,
    )
  }
  return (await response.json()) as unknown
}

/** `GET /portal/:tenantRef/me`. */
export const readPortal = async (config: PortalConfig): Promise<PortalData> =>
  toPortalData(await request(config, '/me'))

/**
 * `GET /portal/:tenantRef/prescriptions/:prescriptionId/copy`. `CUS-007`, `RXW-003`.
 *
 * The domain's own `PrescriptionCopy`, as `readPrescriptionCopy` returns for staff: the server
 * builds one document and both routes return it unchanged.
 */
export const readPortalPrescriptionCopy = async (
  config: PortalConfig,
  prescriptionId: string,
): Promise<PrescriptionCopyData> =>
  (await request(
    config,
    `/prescriptions/${encodeURIComponent(prescriptionId)}/copy`,
  )) as PrescriptionCopyData

/** `GET /portal/:tenantRef/orders/:orderId`. */
export const readPortalOrder = async (
  config: PortalConfig,
  orderId: string,
): Promise<PortalOrderDetailData> =>
  toPortalOrderDetail(await request(config, `/orders/${encodeURIComponent(orderId)}`))
